/**
 * Formatação e vocabulário da tela Consumo de IA, compartilhados pelas três abas.
 *
 * ⚠️ Os slugs ANTIGOS de origem continuam aqui, e não é resíduo: as linhas já gravadas guardam a
 * origem com o nome que ela tinha na época, e remover o rótulo faria o histórico voltar a exibir
 * slug cru — exatamente o defeito que a origem `radar-relacionamento-descoberta` teve desde a
 * contac/at-13 (ela nasceu sem rótulo, `BUG-A` da análise da at-82).
 *
 * Origem de IA nova nasce com o rótulo aqui, no mesmo commit.
 */
const ROTULOS_DE_ORIGEM: Record<string, string> = {
  'assistente-dashboard-oficina': 'Assistente do Dashboard Oficina',
  // Nomes a partir da at-82 (`radar-<id>-<etapa>`, o padrão do núcleo).
  'radar-financeiro-redacao': 'Radar Financeiro - redação dos cards',
  'radar-financeiro-descoberta': 'Radar Financeiro - achados do assistente',
  'radar-relacionamento-redacao': 'Radar do Relacionamento - resumo do painel',
  'radar-relacionamento-descoberta': 'Radar do Relacionamento - achados do assistente',
  // Nomes anteriores à at-82, preservados para o histórico já gravado.
  'radar-redacao': 'Radar Financeiro - redação dos cards',
  'radar-descoberta': 'Radar Financeiro - achados do assistente',
  'radar-relacionamento': 'Radar do Relacionamento - resumo do painel',
};

export function rotuloDeOrigem(origem: string): string {
  return ROTULOS_DE_ORIGEM[origem] ?? origem;
}

/**
 * Quem pediu. 🔴 Linha sem usuário NÃO ganha autor inventado: é o que o sistema faz sozinho (a
 * rotina diária do Radar, a redação dos cartões) e todo registro anterior a 20/10/2026, quando o
 * sistema passou a guardar quem pediu.
 */
export const SEM_USUARIO = 'Sistema (sem usuário)';
export const SEM_USUARIO_EXPLICACAO =
  'Operação sem usuário registrado: a rotina diária do Radar, a redação dos cartões, ou um registro anterior a 20/10/2026, quando o sistema passou a guardar quem pediu.';

export function nomeDoUsuario(usuarioId: string | null, nome: string | null): string {
  if (!usuarioId) return SEM_USUARIO;
  return nome ?? 'Usuário não encontrado';
}

export function fmtInt(n: number): string {
  return n.toLocaleString('pt-BR');
}

export function fmtUsd(n: number): string {
  const casas = n > 0 && n < 0.01 ? 4 : 2;
  return `US$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}`;
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' sem passar por Date (evita deslocamento de fuso). */
export function fmtDia(dia: string): string {
  const [ano, mes, d] = dia.split('-');
  return `${d}/${mes}/${ano}`;
}

/** Instante ISO → 'DD/MM/YYYY HH:mm' no fuso de quem lê (é um instante, não um dia de parede). */
export function fmtQuando(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * `YYYY-MM` → `setembro de 2026`.
 *
 * ⚠️ `timeZone: 'UTC'` é obrigatório: a data é construída em UTC, e sem isso o formatador a
 * converte para o fuso do navegador — o dia 1º às 00h UTC vira o dia 31 do mês ANTERIOR em
 * Brasília, e todo mês apareceria com o nome errado.
 */
export function fmtMes(mes: string): string {
  const [ano, m] = mes.split('-').map(Number);
  const nome = new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(ano, m - 1, 1)),
  );
  return `${nome} de ${ano}`;
}
